// src/App.tsx
import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';

// Import layouts
import MainLayout from './layouts/MainLayout';
import PublicLayout from './layouts/PublicLayout';

// Import debug components (development only)
import DebugButton from './components/debug/DebugButton';
import FirestoreDebugPanel from './components/debug/FirestoreDebugPanel';

// Import services and store actions
import { auth } from './services/firebase/config';
import { checkUserIsAdmin } from './services/firebase/auth';
import { setUser } from './store/slices/authSlice';
import { RootState } from './store';

// Public marketing pages (lazy-loaded)
const HomePage = lazy(() => import('./pages/public/HomePage'));
const FAQPage = lazy(() => import('./pages/public/FAQPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/public/PrivacyPolicyPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));

// Authentication pages
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));

// Protected school portal pages
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ScannerPage = lazy(() => import('./pages/ScannerPage'));
const LostItemsPage = lazy(() => import('./pages/LostItemsPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const PreRegistrationPage = lazy(() => import('./pages/PreRegistrationPage'));
const InquiriesPage = lazy(() => import('./pages/InquiriesPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Route-based document title map for optimal SEO & UX
const TITLE_MAP: Record<string, string> = {
  '/': 'SeekTRU | Smart Lost and Found for Australian Schools',
  '/faq': 'Frequently Asked Questions | SeekTRU',
  '/privacy-policy': 'Privacy Policy & Terms | SeekTRU',
  '/contact': 'Contact Us & Demo Request | SeekTRU',
  '/login': 'School Login | SeekTRU School Portal',
  '/reset-password': 'Reset Password | SeekTRU School Portal',
  '/dashboard': 'Dashboard | SeekTRU School Portal',
  '/scanner': 'Log Found Item(s) | SeekTRU School Portal',
  '/lost-items': 'Found Item(s) History | SeekTRU School Portal',
  '/pre-registration': 'Pre-Register Families | SeekTRU School Portal',
  '/notifications': 'Notifications | SeekTRU School Portal',
  '/settings': 'Settings | SeekTRU School Portal',
  '/inquiries': 'Website Inquiries | SeekTRU Admin Portal',
};

const TitleUpdater: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    document.title = TITLE_MAP[location.pathname] || 'SeekTRU | Smart Lost and Found';
  }, [location.pathname]);

  return null;
};

const LoadingFallback: React.FC = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '60vh' 
    }}
  >
    <CircularProgress />
  </Box>
);

// Protected route for authenticated school & admin users
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useSelector((state: RootState) => state.auth);
  
  if (isLoading) {
    return <LoadingFallback />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Admin-only route for inquiry management
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading, isAdmin } = useSelector((state: RootState) => state.auth);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  
  // Handle authentication state and admin detection
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const isAdmin = await checkUserIsAdmin(user.uid);
          dispatch(setUser({ user, isAdmin }));
        } catch {
          dispatch(setUser({ user, isAdmin: false }));
        }
      } else {
        dispatch(setUser(null));
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [dispatch]);
  
  // Initial authentication loading indicator
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Router>
      <TitleUpdater />
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public Marketing Routes (seektru.com.au) */}
          <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
          <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
          <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><ContactPage /></PublicLayout>} />

          {/* Authentication Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          
          {/* Protected School Portal Routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <MainLayout>
                  <DashboardPage />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/scanner" 
            element={
              <ProtectedRoute>
                <MainLayout>
                  <ScannerPage />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/lost-items" 
            element={
              <ProtectedRoute>
                <MainLayout>
                  <LostItemsPage />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/pre-registration" 
            element={
              <ProtectedRoute>
                <MainLayout>
                  <PreRegistrationPage />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notifications" 
            element={
              <ProtectedRoute>
                <MainLayout>
                  <NotificationsPage />
                </MainLayout>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/settings" 
            element={
              <ProtectedRoute>
                <MainLayout>
                  <SettingsPage />
                </MainLayout>
              </ProtectedRoute>
            } 
          />

          {/* Admin Inquiries Route */}
          <Route 
            path="/inquiries" 
            element={
              <AdminRoute>
                <MainLayout>
                  <InquiriesPage />
                </MainLayout>
              </AdminRoute>
            } 
          />

          {/* Legacy / Helper Redirects */}
          <Route path="/portal" element={<Navigate to="/dashboard" replace />} />
          
          {/* 404 Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      
      {/* Debug tools - only shown in development mode */}
      {process.env.NODE_ENV === 'development' && (
        <>
          <DebugButton />
          <FirestoreDebugPanel />
        </>
      )}
    </Router>
  );
}

export default App;